import React, { ReactNode } from 'react'
import tw from 'twin.macro'
import styled from 'styled-components'
import { useTranslation } from 'contexts/Localization'
import {
  CheckmarkCircleIcon,
  CopyIcon,
  ErrorIcon,
  Flex,
  FlexProps,
  HistoryIcon,
  Tag,
  TagProps,
  Text,
  TextProps,
  useMatchBreakpoints,
  WindIcon,
} from '@nominex/dex-uikit'
import { SlButton } from '@shoelace-style/shoelace/dist/react'
import { formatNumber } from 'utils/formatters'
import { IconWrapper } from 'components/styles'
import { CaptionProps, CommonOverlayProps } from 'components/types'
import LoadableValue from 'components/LoadableValue'

export const Divider = styled.div`
  ${tw`gradient-dark-blue h-[2px] w-[100%]`}
`

export const Row = styled.div<{ withBorder?: boolean }>`
  ${tw`flex py-24 px-0 w-[100%]`}
  ${({ theme, withBorder }) => withBorder && `border-bottom: 1px solid ${theme.colors.darkGray400}`};
`

export const CardCaption: React.FC<CaptionProps> = ({ title, description }) => (
  <div className="flex flex-col pl-[86px]">
    <Text fontSize="16px" bold>
      {title}
    </Text>
    <Text fontSize="14px" color="darkGray50">
      {description}
    </Text>
  </div>
)

export type ColumnProps = {
  caption: string
  last?: boolean
  loading?: boolean
  value: number
} & FlexProps

export const StyledLabel: React.FC<TextProps & { children: ReactNode }> = ({ children, ...props }) => (
  <Text fontSize="11px" color="white" style={{ opacity: 0.7 }} {...props}>
    {children}
  </Text>
)

export const StyledValue: React.FC<TextProps & { loading?: boolean; main?: boolean; children: ReactNode }> = ({
  children,
  loading,
  main = false,
}) => (
  <LoadableValue loading={loading} width="100px" fontSize="14px" bold color={main ? 'green300' : ''} textAlign="end">
    {children} NMX
  </LoadableValue>
)

export const Column: React.FC<ColumnProps> = ({ caption, value, last, loading, children, ...props }) => (
  <Flex flexDirection="column" alignItems="flex-end" minWidth="120px" mt="8px" {...props}>
    <StyledLabel>{caption}</StyledLabel>
    <StyledValue main={last} loading={loading}>
      {formatNumber(value)}
    </StyledValue>
    {children}
  </Flex>
)

export const FirstColumn: React.FC<{ caption: string; body: string | React.ReactNode } & FlexProps> = ({
  caption,
  body,
  ...props
}) => (
  <Flex flexDirection="column" flexGrow="1" {...props}>
    <Text fontSize="14px" bold>
      {caption}
    </Text>
    <Text fontSize="12px" color="white" style={{ opacity: 0.7 }}>
      {body}
    </Text>
  </Flex>
)

export const ClaimStatus: React.FC<TextProps & { isCompleted: boolean }> = ({ isCompleted, ...props }) => {
  const { t } = useTranslation()
  return isCompleted ? (
    <div className="flex items-center">
      <CheckmarkCircleIcon color="green500" width="15px" />
      <Text ml="4px" color="green500" {...props}>
        {t('Completed')}
      </Text>
    </div>
  ) : (
    <div className="flex items-center">
      <HistoryIcon color="burningOrange500" width="15px" />
      <Text ml="4px" color="burningOrange500" {...props}>
        {t('In progress')}
      </Text>
    </div>
  )
}

export const PlatformTag: React.FC<TagProps & { platform: 'cex' | 'dex' }> = ({ platform, ...props }) => (
  <Tag
    variant={platform === 'dex' ? 'translucentPink' : 'translucentPurple'}
    scale="xs"
    fontWeight="600"
    className="notranslate"
    translate="no"
    {...props}
  >
    {platform === 'dex' ? 'DEX' : 'CEX'}
  </Tag>
)

export const TransactionHash: React.FC<TextProps & { isClaimedFromDex: boolean; hash: string }> = ({
  isClaimedFromDex,
  hash,
  ...props
}) => {
  const { t } = useTranslation()
  return isClaimedFromDex ? (
    hash ? (
      <div className="flex items-center gap-8">
        <Text noTranslate {...props}>{`${hash.slice(0, 6)}...${hash.slice(-4)}`}</Text>
        <SlButton className="x-small" variant="text" onClick={() => navigator.clipboard.writeText(hash)}>
          <CopyIcon color="darkGray200" width="14px" />
        </SlButton>
      </div>
    ) : null
  ) : (
    <Text color="darkGray200" {...props}>
      {t('Internal')}
    </Text>
  )
}

export const Overlay: React.FC<CommonOverlayProps & FlexProps> = ({ icon, title, description, children, ...props }) => (
  <Flex flexDirection="column" justifyContent="center" alignItems="center" flexGap="8px" {...props}>
    {icon}
    <div className="flex flex-col items-center gap-8">
      <Text fontSize="16px" bold>
        {title}
      </Text>
      <Text fontSize="14px" color="darkGray100" width="70%" textAlign="center">
        {description}
      </Text>
    </div>
    {children}
  </Flex>
)

export const ErrorOverlay: React.FC<{ onReload: () => void }> = ({ onReload }) => {
  const { t } = useTranslation()
  const { isDesktop } = useMatchBreakpoints()
  return (
    <Overlay
      icon={
        <IconWrapper bgColor="burningOrange500" size="48px">
          <ErrorIcon color="burningOrange500" />
        </IconWrapper>
      }
      title={t('Failed to get data')}
      description={t('Please try to reload later')}
      pt={isDesktop ? '42px' : '18px'}
      pb="18px"
    >
      <SlButton variant="primary" className="mt-12" size="small" onClick={() => onReload()}>
        {t('Reload')}
      </SlButton>
    </Overlay>
  )
}

export const EmptyHistoryOverlay: React.FC = () => {
  const { t } = useTranslation()
  const { isDesktop } = useMatchBreakpoints()
  return (
    <Overlay
      icon={
        <IconWrapper bgColor="blue500" size="48px">
          <WindIcon color="blue500" />
        </IconWrapper>
      }
      title={t('You didn’t claim rewards')}
      description={t('All information about them will be displayed in this table.')}
      pt={isDesktop ? '42px' : '18px'}
      pb="18px"
    />
  )
}

export const HeadRowContainer = styled.div`
  ${tw`text-12 text-gray-900 rounded-6 bg-dark-gray-600`}
`
export const RowContainer = styled.div`
  ${tw`grid p-16 grid-cols-[minmax(200px, 1fr)_minmax(100px, 1fr)_minmax(145px, 1fr)_minmax(155px, 1fr)_minmax(120px, auto)]`}
`
export const RowCell = styled.div`
  ${tw`flex text-12 items-center`}
`
